import React, { useState, useEffect } from 'react'

import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import * as yup from 'yup'

import { RequestUpgradeRequestDTO } from '@/core/api/dtos/request/account/requestUpgrade'
import { CreateZoneRequestDTO } from '@/core/api/dtos/request/zone/create'
import { StatusUpgrade } from '@/core/api/entities/account/statusUpgrade'
import { Subscription } from '@/core/api/entities/account/subscription'
import { SubscriptionPlan } from '@/core/api/entities/account/subscriptionPlan'
import { Zone } from '@/core/api/entities/zone/zone'
import {
	useGetAccountUpgradeStatus,
	useRequestUpgrade,
} from '@/core/hooks/account/useSubscription'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { useLocations } from '@/core/hooks/useLocation'
import { useCreateZone } from '@/core/hooks/useZone'
import { useTranslation } from '@/core/i18n/useTranslation'
import { RootState } from '@/core/store'
import {
	resetZoneCreation,
	setZoneCreationStep,
	setZoneCreationSummary,
} from '@/core/store/slices/zones'
import { Steps } from '@/features/zones/Create/steps/Steps'
import ZoneGrowModal from '@/features/zones/modals/ZoneGrowModal'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'
import { extractErrorMessage } from '@/shared/utils/helpers'
import { formatPhoneNumber } from '@/shared/utils/phoneUtils'

import { CreateZoneUI } from './CreateZoneUI'

export interface ZoneRequestDTO extends CreateZoneRequestDTO {
	document_url?: string | null
	create_new_location: boolean
	has_valid_zone_name: boolean
	// Optional field to track location validation
	location_valid?: boolean
	// Device type selection (app or box)
	device_type?: 'app' | 'box'
}

const Create: React.FC = () => {
	const navigate = useNavigate()
	const { showNotification } = useNotificationContext()

	const { t } = useTranslation('zones')
	const { t: tCommon } = useTranslation('common')
	const dispatch = useDispatch()

	const { user } = useSelector((state: RootState) => state.auth)

	const account = useCurrentAccount()
	const accountId = account?.id || user?.accountId || 0

	// Get step state from Redux
	const currentStep = useSelector<RootState, Steps>(
		(state) => state.zones.createZone.currentStep
	)

	// Load properly the account id on first time loading the component
	useEffect(() => {
		if (accountId === 0 && user?.accountId) {
			form.setValue('account_id', user.accountId)
		}
	}, [user])
	const [firstLoaded, setFirstLoaded] = useState(false)

	// Current subscription
	const [currentSubscription, setCurrentSubscription] =
		useState<Subscription | null>(null)

	// Success state
	const [success] = useState(false)
	const [zoneType, setZoneType] = useState<'single' | 'multiple'>('single')

	// State for the can go next button
	const [canGoNext, setCanGoNext] = useState(false)

	// State for zone grow modal
	const [showZoneGrowModal, setShowZoneGrowModal] = useState(false)

	// Get locations list to access selected location data for box.recipient
	const { data: locationsData } = useLocations({
		config: useAccountConfig({}),
	})

	// Reset zone creation state when component mounts
	useEffect(() => {
		dispatch(resetZoneCreation())
	}, [dispatch])

	// Get account upgrade status for the user or selected account
	const {
		data: accountUpgradeStatus = StatusUpgrade.create({}),
		mutate: getAccountUpgradeStatus,
		isPending: isLoadingStatus,
	} = useGetAccountUpgradeStatus({
		config: useAccountConfig({
			params: {
				include: 'subscriptions.plan.prices,all_subscriptions.plan.prices',
			},
		}),
		options: {
			onSuccess: (data: StatusUpgrade) => {
				setCanGoNext(data.availableLicenses > 0)
				setFirstLoaded(true)
			},
			onError: (error: Error) => {
				showNotification({
					type: 'error',
					message: error.message || tCommon('common.try_again_later'),
				})
			},
		},
	})

	// After the status is loaded, calculate the curent subscription
	useEffect(() => {
		if (accountUpgradeStatus && firstLoaded && !isLoadingStatus) {
			// If we have no subscription then we gonna look for worst case scenarios
			if (accountUpgradeStatus.subscriptions.length === 0) {
				// In the worth case, we are interested in return any other subscription that
				// this account might have because there is a couple of scenarios. For instance:
				// An account might still have licenses to use without the need of upgrading the subscription and
				// that subscription might be fully bonified. This makes the subscription to count as non-valid but
				// should still have the ability to add the zone since the amount of licenses allow the customer to do it.
				if (accountUpgradeStatus.allSubscriptions.length > 0) {
					return setCurrentSubscription(
						accountUpgradeStatus.allSubscriptions[0]
					)
				}

				// If there is no subscription, the we gonna throw an error message because the user should have at least one subscription
				setCanGoNext(false)
				setShowZoneGrowModal(true)

				return
			}

			// If only one is available, return it
			if (accountUpgradeStatus.subscriptions.length === 1) {
				return setCurrentSubscription(accountUpgradeStatus.subscriptions[0])
			}

			// If we have several subscriptions, try to exclude the ones with licenses and return the first one if available
			const filteredSubscriptions = accountUpgradeStatus.subscriptions.filter(
				(subscription) => subscription.hasLicenses
			)
			if (filteredSubscriptions.length > 0) {
				return setCurrentSubscription(filteredSubscriptions[0])
			}

			// Return the first subscription as default one
			return setCurrentSubscription(accountUpgradeStatus.subscriptions[0])
		}
	}, [accountUpgradeStatus])

	// Billing calculation will be done in useEffect after form is declared

	// Create zone mutation
	const { mutate: createZone, isPending: isCreating } = useCreateZone({
		config: useAccountConfig({}),
		options: {
			onSuccess: (zone: Zone) => {
				// Redirect to zones list with the newly created zone ID
				navigate('/zones?tab=zones', {
					state: { newZoneId: zone.id },
				})
			},
			onError: (error: any) => {
				showNotification({
					type: 'error',
					message: t('create.request.error.title'),
					description: extractErrorMessage(
						error,
						tCommon('common.try_again_later')
					),
				})
			},
		},
	})

	// Request upgrade for multiple zones
	const { mutate: requestUpgrade, isPending: isRequestingUpgrade } =
		useRequestUpgrade({
			options: {
				onSuccess: () => {
					showNotification({
						type: 'success',
						message: tCommon('create.request.success.title'),
						description: tCommon('create.request.success.description'),
					})
				},
				onError: (error: Error) => {
					showNotification({
						type: 'error',
						message: tCommon('create.request.error.title'),
						description: error.message || tCommon('common.try_again_later'),
					})
				},
			},
		})

	const defaultData: ZoneRequestDTO = {
		account_id: accountId,
		name: '',
		acknowledgement: false,
		create_new_location: false,
		has_valid_zone_name: false,
		device_type: 'app',
		store: {
			account_id: accountId,
			name: '',
			country: '',
			type: 'own',
			address: '',
			phone_number: '',
			phone_country_code: 'US',
			email: '',
			city: '',
			zip_code: '',
			manager: '',
			timezone: '',
			additional_information: null,
			id_type: '',
			id_number: '',
			// Network settings
			network_type: 'wifi',
			wifi_network: '',
			wifi_password: '',
			network_encryption: 'WEP',
			ip_type: 'DHCP',
			network_additional_info: '',
		},
	}

	// Validation schema
	const validationSchema = yup.object().shape({
		account_id: yup
			.number()
			.required(tCommon('validation.required'))
			.default(defaultData.account_id),
		name: yup
			.string()
			.when('$currentStep', {
				is: (val: number) => val >= Steps.ZoneSettings,
				then: (schema) => schema.required(tCommon('validation.required')),
			})
			.default(defaultData.name),
		store_id: yup
			.number()
			.when('$currentStep', {
				is: (val: number) => val >= Steps.Location,
				then: (schema) => schema.required(tCommon('validation.required')),
			})
			.default(defaultData.store_id),
		acknowledgement: yup
			.boolean()
			.when('$currentStep', {
				is: (val: number) => val === Steps.Confirmation,
				then: (schema) => schema.oneOf([true], tCommon('validation.required')),
			})
			.default(defaultData.acknowledgement),
		device_type: yup
			.string()
			.oneOf(['app', 'box'])
			.when('$currentStep', {
				is: (val: number) => val >= Steps.ZoneSettings,
				then: (schema) => schema.required(tCommon('validation.required')),
			})
			.default(defaultData.device_type),
		document_url: yup.string().nullable(),
		create_new_location: yup.boolean().default(defaultData.create_new_location),
		has_valid_zone_name: yup
			.boolean()
			.when('$currentStep', {
				is: (val: number) => val === 1,
				then: (schema) => schema.isFalse(tCommon('validation.required')),
			})
			.default(defaultData.has_valid_zone_name),
		// Store form fields validation
		store: yup.object().when(['$currentStep', 'create_new_location'], {
			is: (step: number, create_new_location: boolean) => {
				return step === Steps.Location && create_new_location
			},
			then: (schema) =>
				schema.shape({
					name: yup
						.string()
						.min(3, tCommon('validation.minLength', { min: 3 }))
						.max(100, tCommon('validation.maxLength', { max: 100 }))
						.required(tCommon('validation.required'))
						.default(defaultData.store?.name),
					address: yup
						.string()
						.min(6, tCommon('validation.minLength', { min: 6 }))
						.max(255, tCommon('validation.maxLength', { max: 255 }))
						.required(tCommon('validation.required'))
						.default(defaultData.store?.address),
					city: yup
						.string()
						.min(4, tCommon('validation.minLength', { min: 4 }))
						.max(100, tCommon('validation.maxLength', { max: 100 }))
						.required(tCommon('validation.required'))
						.default(defaultData.store?.city),
					zip_code: yup
						.string()
						.min(3, tCommon('validation.minLength', { min: 3 }))
						.max(20, tCommon('validation.maxLength', { max: 20 }))
						.required(tCommon('validation.required'))
						.default(defaultData.store?.zip_code),
					country: yup
						.string()
						.required(tCommon('validation.required'))
						.default(defaultData.store?.country),
					manager: yup
						.string()
						.min(3, tCommon('validation.minLength', { min: 3 }))
						.max(255, tCommon('validation.maxLength', { max: 255 }))
						.required(tCommon('validation.required'))
						.default(defaultData.store?.manager),
					email: yup
						.string()
						.min(8, tCommon('validation.minLength', { min: 8 }))
						.max(100, tCommon('validation.maxLength', { max: 100 }))
						.email(tCommon('validation.email'))
						.required(tCommon('validation.required'))
						.default(defaultData.store?.email),
					phone_number: yup
						.string()
						.min(8, tCommon('validation.minLength', { min: 8 }))
						.max(40, tCommon('validation.maxLength', { max: 40 }))
						// .matches(/^\d*$/i, tCommon('validation.numbersOnly'))
						.required(tCommon('validation.required'))
						.default(defaultData.store?.phone_number),
					type: yup
						.string()
						.required(tCommon('validation.required'))
						.default(defaultData.store?.type),
					timezone: yup
						.string()
						.required(tCommon('validation.required'))
						.default(defaultData.store?.timezone),
					additional_information: yup
						.string()
						.nullable()
						.max(255, tCommon('validation.maxLength', { max: 255 }))
						.default(defaultData.store?.additional_information),
					phone_country_code: yup
						.string()
						.nullable()
						.default(defaultData.store?.phone_country_code),
					id_type: yup
						.string()
						.nullable()
						.notRequired()
						.default(defaultData.store?.id_type),
					id_number: yup
						.string()
						.nullable()
						.notRequired()
						.default(defaultData.store?.id_number),
				}),
		}),
	})

	// Form
	const form = useForm<ZoneRequestDTO>({
		defaultValues: defaultData,
		resolver: yupResolver(validationSchema) as any, // TODO: Remove this casting
		context: { currentStep, zoneType },
	})

	// Get account upgrade status for the user or selected account
	useEffect(() => {
		if (accountId) {
			getAccountUpgradeStatus({
				account_id: accountId,
				with_box: false,
			})
		}
	}, [accountId])

	// Reset form values when zone type changes
	useEffect(() => {
		form.setValue('document_url', '')
		form.setValue('name', '')
		form.setValue('store_id', undefined)
		form.setValue('has_valid_zone_name', false)
	}, [zoneType])

	const deviceType = form.watch('device_type')
	// Calculate billing summary when subscription or device type changes
	useEffect(() => {
		if (currentSubscription) {
			const subscriptionPlan = currentSubscription.plan as SubscriptionPlan

			const zonePrice = currentSubscription.getZonePrice() ?? 0
			const boxPrice =
				currentSubscription.getBoxPrice(
					accountUpgradeStatus,
					subscriptionPlan.prices
				) ?? 0
			const soundreefPrice = subscriptionPlan.getSoundreefPrice()

			let newZones = 0
			let newTotalZones = accountUpgradeStatus.getTotalZones()
			if (newTotalZones > 0) {
				newZones++
				newTotalZones += accountUpgradeStatus.willUpdateSubscription
					? newZones
					: 0
			}
			let newBoxes = 0
			let newTotalBoxes = accountUpgradeStatus.getTotalBoxes()
			if (newTotalBoxes) {
				newBoxes++
				newTotalBoxes += +(accountUpgradeStatus.hasBoxMode &&
				deviceType === 'box'
					? newBoxes
					: 0)
			}
			let newSoundreef = 0
			let newTotalSoundreef = accountUpgradeStatus.getTotalLicenses()
			if (newTotalSoundreef) {
				newSoundreef++
				newTotalSoundreef += accountUpgradeStatus.willUpdateSubscription
					? newSoundreef
					: 0
			}

			const total =
				zonePrice * newTotalZones +
				boxPrice * newTotalBoxes +
				(soundreefPrice?.price ?? 0) * newTotalSoundreef

			dispatch(
				setZoneCreationSummary({
					zonePrice,
					boxPrice,
					soundreefPrice: soundreefPrice?.price ?? 0,
					newTotalZones,
					newTotalBoxes,
					newTotalSoundreef,
					newZones,
					newBoxes,
					newSoundreef,
					total,
				})
			)
		}
	}, [currentSubscription, accountUpgradeStatus, form, dispatch, deviceType])

	// Handle form data changes
	const handleFormDataChange = (data: Partial<ZoneRequestDTO>) => {
		// Enable the "Next" button at certain steps
		if (currentStep === Steps.ZoneSettings) {
			// Step 0: For single zone, require name, location, and device type
			const hasName = !!form.watch('name')
			const hasLocation = !!data.store_id || !!form.watch('store_id')
			const hasValidZoneName =
				!!data.has_valid_zone_name || !!form.watch('has_valid_zone_name')
			const hasDeviceType = !!data.device_type || !!form.watch('device_type')

			form.setValue('create_new_location', !!data.create_new_location)
			setCanGoNext(hasName && hasLocation && hasValidZoneName && hasDeviceType)
		} else if (currentStep === Steps.Location) {
			// Step 1: For location form, all required fields must be valid
			setCanGoNext(!!data.location_valid)
		} else if (currentStep === Steps.NetworkConfig) {
			// Step 2: Network Config - enable next if basic fields are filled
			// More detailed validation happens in handleNext()
			const isNewLocation = form.watch('store_id') === -1
			const networkType = isNewLocation
				? form.watch('store.network_type')
				: form.watch('box.connection_settings.connection_type')

			setCanGoNext(!!networkType)
		} else if (currentStep === Steps.Confirmation) {
			// Step 3: Confirmation - always allow submit
			setCanGoNext(true)
		}
	}

	// Handle form submission
	const handleSubmit = (data: ZoneRequestDTO | RequestUpgradeRequestDTO) => {
		// For multiple zones
		if (zoneType === 'multiple') {
			// Submit upgrade request
			requestUpgrade({
				document_url: data.document_url,
				account_id: user?.account?.id || accountId,
				with_box: false,
			})
		}
		// For single zone flow
		else {
			const zoneData = data as ZoneRequestDTO
			const isNewLocation = zoneData.store_id === -1
			const isBox = zoneData.device_type === 'box'

			// Handle new location
			if (isNewLocation && zoneData.store) {
				// Format phone number
				zoneData.store.phone_number = formatPhoneNumber(
					zoneData.store.phone_country_code,
					zoneData.store.phone_number
				)

				// If Box is selected for new location, network settings are already in store
				// No need to populate box field for new locations

				// Remove store_id for new locations - the API will create it
				delete zoneData.store_id
			} else {
				// Existing location - delete store object
				delete zoneData.store
			}

			// Handle box data for existing location + box
			if (isBox && !isNewLocation) {
				// Find the selected location to populate box.recipient
				const selectedLocation = locationsData?.data?.find(
					(loc) => loc.id === zoneData.store_id
				)

				if (!selectedLocation) {
					showNotification({
						type: 'error',
						message: tCommon('create.validation.locationNotFound'),
						description: tCommon('create.validation.selectValidLocation'),
					})
					return
				}

				// Initialize box object if it doesn't exist
				if (!zoneData.box) {
					zoneData.box = {} as any
				}

				// Populate box.recipient with location data (always override with location data)
				zoneData.box!.recipient = {
					name: selectedLocation.manager || selectedLocation.name,
					address: selectedLocation.address || '',
					city: selectedLocation.city || '',
					zip_code: selectedLocation.zipCode || '',
					country: selectedLocation.country,
					identification_type: selectedLocation.idType || '',
					identification_number: selectedLocation.idNumber || '',
					phone: selectedLocation.phoneNumber || '',
					additional_information: selectedLocation.additionalInformation,
				}

				// Initialize connection_settings with defaults if not provided by form
				if (!zoneData.box!.connection_settings) {
					zoneData.box!.connection_settings = {
						connection_type: 'WIFI',
						additional_information: null,
						network: null,
						password: null,
						type_of_network: 'WPA2',
						ip_protocol_assignment: 'DHCP',
						ip_address: null,
						dns_1: null,
						dns_2: null,
						netmask: null,
						gateway: null,
						ntp_address: null,
						proxy_address: null,
						proxy_port: null,
					}
				}

				// Map box.connection_settings to the API format
				// The box field should already be populated by the form
				// Ensure it's properly formatted
				if (zoneData.box?.connection_settings) {
					const connectionSettings = zoneData.box.connection_settings

					// Clean up null/undefined values for optional fields
					if (!connectionSettings.dns_2) {
						delete connectionSettings.dns_2
					}
					if (!connectionSettings.ntp_address) {
						delete connectionSettings.ntp_address
					}
					if (!connectionSettings.proxy_address) {
						delete connectionSettings.proxy_address
					}
					if (!connectionSettings.proxy_port) {
						delete connectionSettings.proxy_port
					}

					// For DHCP, remove static IP fields
					if (connectionSettings.ip_protocol_assignment === 'DHCP') {
						delete connectionSettings.ip_address
						delete connectionSettings.gateway
						delete connectionSettings.netmask
						delete connectionSettings.dns_1
						delete connectionSettings.dns_2
					}

					// For Ethernet, remove WiFi fields
					if (connectionSettings.connection_type === 'ETHERNET') {
						delete connectionSettings.network
						delete connectionSettings.password
						delete connectionSettings.type_of_network
					}
				}
			}

			// Clean up temporary fields
			delete (zoneData as any).create_new_location
			delete (zoneData as any).has_valid_zone_name
			delete (zoneData as any).location_valid
			delete (zoneData as any).device_type

			// Create a single zone
			createZone(zoneData)
		}
	}

	// Handle navigation between steps - New logic for 4 cases
	const handleNext = () => {
		const deviceType = form.watch('device_type')
		const isNewLocation = form.watch('store_id') === -1
		const isBox = deviceType === 'box'

		/**
		 * 4 Navigation Cases:
		 * 1. Existing location + App:  Step 0 → Confirmation
		 * 2. Existing location + Box:  Step 0 → NetworkConfig → Confirmation
		 * 3. New location + App:       Step 0 → Location → Confirmation
		 * 4. New location + Box:       Step 0 → Location → NetworkConfig → Confirmation
		 */

		if (currentStep === Steps.ZoneSettings) {
			// Step 0: Zone Setup - validate and determine next step
			form.trigger(['store_id', 'name', 'device_type']).then((isValid) => {
				if (isValid) {
					if (isNewLocation) {
						// Cases 3 & 4: Go to Location step
						dispatch(setZoneCreationStep(Steps.Location))
					} else if (isBox) {
						// Case 2: Existing location + Box → Go to NetworkConfig
						dispatch(setZoneCreationStep(Steps.NetworkConfig))
					} else {
						// Case 1: Existing location + App → Skip to Confirmation
						dispatch(setZoneCreationStep(Steps.Confirmation))
					}
				}
			})
		} else if (currentStep === Steps.Location) {
			// Step 1: Location - validate and determine next step
			form
				.trigger([
					'store.name',
					'store.country',
					'store.address',
					'store.city',
					'store.zip_code',
					'store.timezone',
					'store.manager',
					'store.id_type',
					'store.id_number',
					'store.email',
					'store.phone_number',
				])
				.then((isValid) => {
					if (isValid) {
						if (isBox) {
							// Case 4: New location + Box → Go to NetworkConfig
							dispatch(setZoneCreationStep(Steps.NetworkConfig))
						} else {
							// Case 3: New location + App → Skip to Confirmation
							dispatch(setZoneCreationStep(Steps.Confirmation))
						}
					}
				})
		} else if (currentStep === Steps.NetworkConfig) {
			// Step 2: Network Configuration - validate and go to Confirmation
			// Validate network fields based on connection type and IP type
			const networkType = (form.watch(
				'box.connection_settings.connection_type'
			) ||
				form.watch('store.network_type') ||
				'') as string
			const ipType = (form.watch(
				'box.connection_settings.ip_protocol_assignment'
			) ||
				form.watch('store.ip_type') ||
				'') as string
			const isWifi = networkType === 'WIFI' || networkType === 'wifi'
			const isStatic = ipType === 'STATIC' || ipType === 'Static IP'

			// Build validation fields dynamically
			const fieldsToValidate: string[] = []

			if (isNewLocation) {
				// For new location, validate store fields
				if (isWifi) {
					fieldsToValidate.push('store.wifi_network', 'store.wifi_password')
				}
				if (isStatic) {
					fieldsToValidate.push(
						'store.ip_address',
						'store.gateway',
						'store.netmask',
						'store.dns_1'
					)
				}
			} else {
				// For existing location, validate box fields
				if (isWifi) {
					fieldsToValidate.push(
						'box.connection_settings.network',
						'box.connection_settings.password'
					)
				}
				if (isStatic) {
					fieldsToValidate.push(
						'box.connection_settings.ip_address',
						'box.connection_settings.gateway',
						'box.connection_settings.netmask',
						'box.connection_settings.dns_1'
					)
				}
			}

			form
				.trigger(
					fieldsToValidate.length > 0 ? (fieldsToValidate as any) : undefined
				)
				.then((isValid) => {
					if (isValid) {
						dispatch(setZoneCreationStep(Steps.Confirmation))
					}
				})
		} else if (currentStep === Steps.Confirmation) {
			// Step 3: Confirmation - submit form
			handleSubmit(form.getValues())
		}
	}

	const handleBack = () => {
		const deviceType = form.watch('device_type')
		const isNewLocation = form.watch('store_id') === -1
		const isBox = deviceType === 'box'

		if (currentStep === Steps.ZoneSettings) {
			// Step 0: Go back to zones list
			navigate('/zones')
		} else if (currentStep === Steps.Location) {
			// Step 1: Location → Go back to Zone Settings
			dispatch(setZoneCreationStep(Steps.ZoneSettings))
		} else if (currentStep === Steps.NetworkConfig) {
			// Step 2: Network Config → Go back based on whether location is new
			if (isNewLocation) {
				// Case 4: New location + Box → Go back to Location
				dispatch(setZoneCreationStep(Steps.Location))
			} else {
				// Case 2: Existing location + Box → Go back to Zone Settings
				dispatch(setZoneCreationStep(Steps.ZoneSettings))
			}
		} else if (currentStep === Steps.Confirmation) {
			// Step 3: Confirmation → Go back based on flow
			if (isBox) {
				// Cases 2 & 4: Box selected → Go back to NetworkConfig
				dispatch(setZoneCreationStep(Steps.NetworkConfig))
			} else if (isNewLocation) {
				// Case 3: New location + App → Go back to Location
				dispatch(setZoneCreationStep(Steps.Location))
			} else {
				// Case 1: Existing location + App → Go back to Zone Settings
				dispatch(setZoneCreationStep(Steps.ZoneSettings))
			}
		}
	}

	// Handle cancel
	const handleCancel = () => {
		navigate('/zones')
	}

	// Total number of steps
	const totalSteps = 4

	return (
		<>
			<CreateZoneUI
				currentStep={currentStep}
				totalSteps={totalSteps}
				zoneType={zoneType}
				setZoneType={setZoneType}
				form={form}
				onFormDataChange={handleFormDataChange}
				onSubmit={handleSubmit}
				onNext={handleNext}
				onBack={handleBack}
				onCancel={handleCancel}
				canGoNext={canGoNext}
				isCreating={isCreating || isRequestingUpgrade}
				isLoadingStatus={isLoadingStatus}
				accountUpgradeStatus={accountUpgradeStatus}
				user={user || null}
				success={success}
				currentSubscription={currentSubscription}
			/>
			<ZoneGrowModal
				visible={showZoneGrowModal}
				onClose={() => setShowZoneGrowModal(false)}
				onContactMe={() => {
					// Trigger the requestUpgrade mutation
					requestUpgrade({
						document_url: null,
						account_id: accountId,
						with_box: false,
					})
					setShowZoneGrowModal(false)
				}}
				isRequestingUpgrade={isRequestingUpgrade}
			/>
		</>
	)
}

export default Create
